import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapPin, Flame, ArrowRight, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

/**
 * Horizontal ribbon that surfaces 4 hyper-local schemes for the user's state.
 * Logged-in users land on their own state (from profile); guests land on AP
 * (our largest dataset) but can switch via the dropdown. Only rendered when
 * the user hasn't already applied a state filter — otherwise it just duplicates
 * the main grid.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORTED_STATES = ['Andhra Pradesh', 'Telangana', 'Karnataka', 'Central'];
const STORAGE_KEY = 'kms_popular_state_pref';

const PopularInYourState = ({ currentUser, onApplyStateFilter }) => {
  const navigate = useNavigate();

  // Default: logged-in user's state (if supported) → localStorage → AP
  const defaultState = useMemo(() => {
    const userState = currentUser?.state;
    if (userState && SUPPORTED_STATES.includes(userState)) return userState;
    const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved && SUPPORTED_STATES.includes(saved)) return saved;
    return 'Andhra Pradesh';
  }, [currentUser]);

  const [activeState, setActiveState] = useState(defaultState);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/schemes`, { params: { state: activeState, limit: 4 } })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : res.data?.schemes || [];
        setSchemes(rows.slice(0, 4));
      })
      .catch(() => !cancelled && setSchemes([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [activeState]);

  const handleStateChange = (next) => {
    setActiveState(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  if (!loading && schemes.length === 0) return null;

  return (
    <div className="mb-6" data-testid="popular-in-your-state">
      <Card className="p-4 sm:p-5 border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white flex-shrink-0">
              <Flame className="h-4 w-4" />
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm sm:text-base font-bold text-slate-900">Popular in</span>
              <Select value={activeState} onValueChange={handleStateChange}>
                <SelectTrigger
                  className="h-8 w-auto min-w-[140px] text-sm font-bold border-emerald-300 bg-white"
                  data-testid="popular-state-select"
                >
                  <MapPin className="h-3.5 w-3.5 text-emerald-700 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={() => onApplyStateFilter?.(activeState)}
            variant="ghost"
            className="h-8 text-sm text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 font-semibold px-3"
            data-testid="popular-see-all"
          >
            See all <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {/* Scheme cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {schemes.map((s, i) => (
              <button
                key={s.id || s.slug}
                onClick={() => navigate(`/schemes/${s.slug || s.id}`)}
                className="group text-left rounded-xl border border-slate-200 bg-white p-3.5 hover:border-emerald-400 hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`popular-scheme-${i}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 border border-emerald-200">
                    <Sparkles className="h-3 w-3 text-emerald-700" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                      {s.sector || 'Scheme'}
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
                <h4 className="text-sm font-bold text-slate-900 line-clamp-2 leading-snug">
                  {s.name}
                </h4>
                {s.shortDescription && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-snug">
                    {s.shortDescription}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default PopularInYourState;
